import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, MapPin, Calendar, Users, Trophy } from "lucide-react";
import AdBanner from "@/components/AdBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const fmtDate = (d, end) => {
  if (!d) return "";
  const opts = { month: "long", day: "numeric", year: "numeric" };
  const s = new Date(`${d}T12:00:00`).toLocaleDateString(undefined, opts);
  return end && end !== d ? `${s} – ${new Date(`${end}T12:00:00`).toLocaleDateString(undefined, opts)}` : s;
};

export const isMoneyball = (t) => (t.tiers || []).includes("Moneyball") || `${t.name} ${t.description || ""}`.toLowerCase().includes("moneyball");

export const eventUrl = (t) => {
  const slug = `${t.name}-${t.location || ""}`.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);
  return `/events/${slug}-${t.id}`;
};

export const isPast = (t) => t.status === "completed" || t.status === "complete";

export function EventCard({ t }) {
  const past = isPast(t);
  return (
    <Link to={eventUrl(t)} data-testid={`event-card-${t.id}`}
      className={`block border p-5 transition-transform duration-200 hover:-translate-y-1 bg-white/[0.03] ${past ? "border-zinc-800 opacity-70" : "border-lime-400/30"}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {isMoneyball(t) && <span className="text-[9px] font-black uppercase tracking-widest bg-amber-400 text-black px-2 py-0.5">💰 Moneyball</span>}
        <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 border ${past ? "border-zinc-700 text-zinc-500" : "border-lime-400/50 text-lime-300"}`}>
          {past ? "Completed" : t.status === "open" ? "Registration Open" : t.status}
        </span>
      </div>
      <h3 className="font-display font-bold uppercase text-lg leading-tight mb-2">{t.name}</h3>
      <div className="space-y-1 text-xs text-zinc-400">
        <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-lime-400" /> {fmtDate(t.date, t.end_date)}</div>
        <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-lime-400" /> {t.location}</div>
        <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-lime-400" /> {t.entry_count} registered{t.entry_fee > 0 ? ` · $${t.entry_fee} entry` : " · Free entry"}</div>
      </div>
    </Link>
  );
}

export default function PublicEvents() {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/events`).then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  }, []);

  if (!events) return null;
  const upcoming = events.filter((t) => !isPast(t));
  const past = events.filter((t) => isPast(t)).reverse();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 py-10" data-testid="public-events-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/" data-testid="events-back-link"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 hover:text-lime-400 transition-colors mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> Pickle Bullpen
        </Link>
        <p className="font-mono text-lime-300 uppercase tracking-[0.3em] text-xs mb-4">Bradenton · Sarasota · Tampa Bay</p>
        <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl mb-3">
          Pickleball Tournaments &amp; Moneyball Events
        </h1>
        <p className="text-zinc-400 max-w-2xl mb-12">
          Every Pickle Bullpen tournament, league night and Moneyball event — dates, locations, entry fees and live
          registration counts. Winners get celebrated, champions get bounty cards, and every match feeds your Player Skill Index.
        </p>
        <AdBanner zone="events" />

        {upcoming.length === 0 && past.length === 0 && (
          <div className="border border-dashed border-zinc-800 p-14 text-center text-zinc-600 uppercase tracking-widest text-sm" data-testid="events-empty">
            No events posted yet — new tournaments and Moneyball events drop here first.
          </div>
        )}

        {upcoming.length > 0 && (
          <div className="mb-14" data-testid="events-upcoming">
            <h2 className="font-display font-extrabold uppercase tracking-wide text-lime-300 text-2xl mb-6">Upcoming Events</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {upcoming.map((t) => <EventCard key={t.id} t={t} />)}
            </div>
          </div>
        )}

        {past.length > 0 && (
          <div className="mb-14" data-testid="events-past">
            <h2 className="flex items-center gap-2 font-display font-extrabold uppercase tracking-wide text-zinc-400 text-2xl mb-6">
              <Trophy className="w-5 h-5" /> Past Events
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {past.map((t) => <EventCard key={t.id} t={t} />)}
            </div>
          </div>
        )}

        <div className="mb-14 border-t border-zinc-900 pt-8" data-testid="events-city-links">
          <h2 className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Find Pickleball Tournaments Near You</h2>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-zinc-400">
            {[["bradenton", "Bradenton"], ["sarasota", "Sarasota"], ["tampa", "Tampa"], ["lakewood-ranch", "Lakewood Ranch"], ["ellenton", "Ellenton"], ["st-petersburg", "St. Petersburg"]].map(([slug, name]) => (
              <Link key={slug} to={`/pickleball-tournaments/${slug}`} className="hover:text-lime-400 transition-colors underline underline-offset-4 decoration-zinc-700">
                Pickleball Tournaments in {name}
              </Link>
            ))}
          </div>
        </div>

        <div className="text-center border-t border-zinc-900 pt-10">
          <p className="text-zinc-400 mb-4">Ready to compete? Create your free player profile and register in minutes.</p>
          <Link to="/auth?mode=register" data-testid="events-join-btn"
            className="inline-flex items-center gap-2 rounded-full bg-lime-400 text-black font-display font-bold uppercase tracking-wider px-8 py-3 hover:bg-lime-300 transition-colors">
            Enter the Bullpen
          </Link>
        </div>
      </div>
    </div>
  );
}
